import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { fetchPaged } from "@/api/paged";
import { cn } from "@/lib/utils";
import {
  ACESSOS_KEY,
  INSTITUICOES_PATH,
  USUARIOS_PATH,
  type AcessoInstituicao,
  type AcessoUsuario,
  type FiltroAcessoInstituicao,
  type FiltroAcessoUsuario,
  type Periodo,
  type StatusInstituicao,
} from "./api";
import { Campo, StatusBadge } from "./componentes";
import {
  SITUACAO_ADMIN_LABEL,
  SITUACOES_ADMIN,
  SITUACOES_PADRAO,
  STATUS_INFO,
  formatAcesso,
  formatDiasSemAcesso,
  formatNumero,
  formatPercentual,
  tendencia,
} from "./formato";

const STATUS_INSTITUICAO: StatusInstituicao[] = ["OK", "ATENCAO", "INATIVA", "NUNCA"];

/** Valor do Select de situação: "padrao" = Ativas + Trial; "todas" = sem filtro. */
function situacoesDoSelect(v: string): string[] | undefined {
  if (v === "padrao") return SITUACOES_PADRAO;
  if (v === "todas") return undefined;
  return [v];
}

function selectDasSituacoes(s?: string[]): string {
  if (!s?.length) return "todas";
  if (s.length === 1) return s[0]!;
  return "padrao";
}

export function Tendencia({ atual, anterior }: { atual: number; anterior: number }) {
  const t = tendencia(atual, anterior);
  return (
    <span
      className={cn("text-xs tabular-nums", t.sinal > 0 ? "text-emerald-600" : t.sinal < 0 ? "text-red-600" : "text-muted-foreground")}
      title={`Período anterior: ${formatNumero(anterior)}`}
    >
      {t.sinal > 0 ? "▲ " : t.sinal < 0 ? "▼ " : ""}
      {t.texto}
    </span>
  );
}

const columns: ColumnDef<AcessoInstituicao, unknown>[] = [
  {
    accessorKey: "nomeFantasia",
    header: "Instituição",
    cell: ({ row }) => (
      <div className="min-w-[12rem]">
        <Link to={`/Acessos/Instituicao/${row.original.id}`} className="font-medium text-foreground hover:underline">
          {row.original.nomeFantasia ?? row.original.razaoSocial ?? `#${row.original.id}`}
        </Link>
        <div className="text-xs text-muted-foreground">
          {[row.original.codigo, SITUACAO_ADMIN_LABEL[row.original.situacaoAdmin ?? ""]].filter(Boolean).join(" · ")}
        </div>
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <div className="flex flex-col items-start gap-1">
        <StatusBadge status={row.original.status} tipo="instituicao" />
        {row.original.reativada && <span className="text-xs text-emerald-700">reativada</span>}
      </div>
    ),
  },
  {
    accessorKey: "ultimoAcesso",
    header: "Último acesso",
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        {formatAcesso(row.original.ultimoAcesso)}
        {row.original.ultimoAcessoUsuario && <div className="text-xs text-muted-foreground">{row.original.ultimoAcessoUsuario}</div>}
      </div>
    ),
  },
  {
    accessorKey: "diasSemAcesso",
    header: "Sem acesso",
    cell: ({ row }) => <span className="whitespace-nowrap">{formatDiasSemAcesso(row.original.diasSemAcesso)}</span>,
  },
  {
    accessorKey: "primeiroAcesso",
    header: "Primeiro acesso",
    cell: ({ row }) => <PrimeiroAcesso valor={row.original.primeiroAcesso} antesDoLog={row.original.primeiroAcessoAntesDoLog} />,
  },
  {
    accessorKey: "logins",
    header: "Logins",
    cell: ({ row }) => (
      <div className="flex flex-col tabular-nums">
        {formatNumero(row.original.logins)}
        <Tendencia atual={row.original.logins} anterior={row.original.loginsPeriodoAnterior} />
      </div>
    ),
  },
  { accessorKey: "diasAtivos", header: "Dias ativos", cell: ({ row }) => <span className="tabular-nums">{row.original.diasAtivos}</span> },
  {
    accessorKey: "cobertura",
    header: "Usuários que acessaram",
    cell: ({ row }) => (
      <div className="whitespace-nowrap tabular-nums">
        {row.original.usuariosAcessaram} de {row.original.usuariosAtivos}
        <div className="text-xs text-muted-foreground">{formatPercentual(row.original.cobertura)}</div>
      </div>
    ),
  },
  {
    accessorKey: "concentracao",
    header: "Concentração",
    cell: ({ row }) => (
      <div className="flex flex-col items-start gap-1 tabular-nums">
        {formatPercentual(row.original.concentracao)}
        {row.original.dependeUmUsuario && (
          <span
            className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
            title="O usuário que mais acessa faz 70% ou mais dos logins"
          >
            depende de 1 usuário
          </span>
        )}
      </div>
    ),
  },
  {
    accessorKey: "loginsMobile",
    header: "App",
    cell: ({ row }) => (
      <span className="tabular-nums" title={`${row.original.loginsWeb} web · ${row.original.loginsMobile} app`}>
        {row.original.logins ? formatPercentual(row.original.loginsMobile / row.original.logins) : "—"}
      </span>
    ),
  },
];

export function PrimeiroAcesso({ valor, antesDoLog }: { valor?: string; antesDoLog?: boolean }) {
  if (!valor) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className="whitespace-nowrap"
      title={antesDoLog ? "O histórico de logins começa depois do cadastro: o primeiro acesso real pode ser anterior." : undefined}
    >
      {antesDoLog && "≤ "}
      {formatAcesso(valor)}
    </span>
  );
}

export function AbaInstituicoes({ periodo }: { periodo: Periodo }) {
  const navigate = useNavigate();
  const query = usePagedQuery<AcessoInstituicao, FiltroAcessoInstituicao>({
    key: [...ACESSOS_KEY, "instituicoes"],
    path: INSTITUICOES_PATH,
    initialFiltro: { situacoes: SITUACOES_PADRAO },
    extraFiltro: periodo,
  });

  const actions: RowAction<AcessoInstituicao>[] = [
    { label: "Ver acessos", icon: BarChart3, onClick: (row) => navigate(`/Acessos/Instituicao/${row.id}`) },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <TopInstituicoes periodo={periodo} />
        <TopUsuarios periodo={periodo} />
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="acessos-instituicoes"
        emptyMessage="Nenhuma instituição encontrada."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <Campo id="fi-busca" label="Instituição / CNPJ" className="w-64">
              <Input
                id="fi-busca"
                value={query.draft.busca ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, busca: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </Campo>
            <Campo id="fi-situacao" label="Situação" className="w-40">
              <Select
                value={selectDasSituacoes(query.draft.situacoes)}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, situacoes: situacoesDoSelect(v) }))}
              >
                <SelectTrigger id="fi-situacao">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="padrao">Ativas + Trial</SelectItem>
                  {SITUACOES_ADMIN.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="todas">Todas</SelectItem>
                </SelectContent>
              </Select>
            </Campo>
            <Campo id="fi-status" label="Status de acesso" className="w-44">
              <Select
                value={query.draft.status?.[0] ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, status: v === "__all__" ? undefined : [v as StatusInstituicao] }))}
              >
                <SelectTrigger id="fi-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {STATUS_INSTITUICAO.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_INFO[s].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
            <label className="flex h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={!!query.draft.dependeUmUsuario}
                onCheckedChange={(c) => query.setDraft((f) => ({ ...f, dependeUmUsuario: c === true || undefined }))}
              />
              Depende de 1 usuário
            </label>
            <label className="flex h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={!!query.draft.reativada}
                onCheckedChange={(c) => query.setDraft((f) => ({ ...f, reativada: c === true || undefined }))}
              />
              Reativadas
            </label>
            <Button onClick={() => query.search()}>Buscar</Button>
          </div>
        }
      />
    </div>
  );
}

const LIMITE_TOP = 10;

function useTop<T, F>(tipo: string, path: string, filtro: F) {
  return useQuery({
    queryKey: [...ACESSOS_KEY, "top", tipo, filtro],
    queryFn: ({ signal }) =>
      fetchPaged<T, F>(path, { page: 0, size: LIMITE_TOP, sort: { field: "logins", direction: "DESC" }, filtro }, signal),
  });
}

function TopInstituicoes({ periodo }: { periodo: Periodo }) {
  const top = useTop<AcessoInstituicao, FiltroAcessoInstituicao>("instituicoes", INSTITUICOES_PATH, {
    ...periodo,
    situacoes: SITUACOES_PADRAO,
  });
  const linhas = (top.data?.content ?? []).filter((i) => i.logins > 0);
  return (
    <CardTop titulo="Top instituições" isLoading={top.isLoading} vazio={linhas.length === 0}>
      {linhas.map((i, n) => (
        <TableRow key={i.id}>
          <TableCell className="w-8 text-muted-foreground">{n + 1}</TableCell>
          <TableCell className="font-medium">
            <Link to={`/Acessos/Instituicao/${i.id}`} className="hover:underline">
              {i.nomeFantasia ?? `#${i.id}`}
            </Link>
          </TableCell>
          <TableCell className="text-right tabular-nums">{formatNumero(i.logins)}</TableCell>
          <TableCell className="text-right tabular-nums text-muted-foreground">
            {i.usuariosAcessaram}/{i.usuariosAtivos}
          </TableCell>
        </TableRow>
      ))}
    </CardTop>
  );
}

function TopUsuarios({ periodo }: { periodo: Periodo }) {
  const top = useTop<AcessoUsuario, FiltroAcessoUsuario>("usuarios", USUARIOS_PATH, {
    ...periodo,
    situacoesInstituicao: SITUACOES_PADRAO,
  });
  const linhas = (top.data?.content ?? []).filter((u) => u.logins > 0);
  return (
    <CardTop titulo="Top usuários" segunda="Dias ativos" isLoading={top.isLoading} vazio={linhas.length === 0}>
      {linhas.map((u, n) => (
        <TableRow key={u.id}>
          <TableCell className="w-8 text-muted-foreground">{n + 1}</TableCell>
          <TableCell>
            <div className="font-medium">{u.nome ?? u.login}</div>
            {u.idInstituicao && (
              <Link to={`/Acessos/Instituicao/${u.idInstituicao}`} className="text-xs text-muted-foreground hover:underline">
                {u.nomeInstituicao}
              </Link>
            )}
          </TableCell>
          <TableCell className="text-right tabular-nums">{formatNumero(u.logins)}</TableCell>
          <TableCell className="text-right tabular-nums text-muted-foreground">{u.diasAtivos}</TableCell>
        </TableRow>
      ))}
    </CardTop>
  );
}

function CardTop({
  titulo,
  segunda = "Usuários",
  isLoading,
  vazio,
  children,
}: {
  titulo: string;
  segunda?: string;
  isLoading: boolean;
  vazio: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <h2 className="mb-3 text-base font-semibold text-foreground">{titulo}</h2>
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : vazio ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Nenhum login no período.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">#</TableHead>
              <TableHead>{titulo.replace("Top ", "").replace(/^./, (c) => c.toUpperCase())}</TableHead>
              <TableHead className="text-right">Logins</TableHead>
              <TableHead className="text-right">{segunda}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{children}</TableBody>
        </Table>
      )}
    </section>
  );
}
